<template>
  <Context
    ref="context"
    class="rich-text-editor__context-menu"
    :hide-on-scroll="true"
    :hide-on-resize="true"
    @hidden="onHidden"
  >
    <div role="menu" @mousedown.prevent @keydown="onKeydown">
      <ul class="context__menu">
        <li v-for="item in blockItems" :key="item.type + (item.level || '')">
          <button
            type="button"
            role="menuitemradio"
            class="context__menu-item-link rich-text-editor__context-menu-item"
            :aria-checked="isActive(item)"
            @click="apply(item)"
          >
            <i :class="item.icon" class="context__menu-item-icon"></i>
            <span>{{ $t(`richTextEditorFloatingMenu.${item.label}`) }}</span>
            <span
              class="rich-text-editor__context-menu-preview"
              aria-hidden="true"
            >
              {{ item.preview }}
            </span>
          </button>
        </li>
      </ul>
      <ul v-if="hasSelection" class="context__menu">
        <li v-for="item in markItems" :key="item.type">
          <button
            type="button"
            role="menuitemcheckbox"
            class="context__menu-item-link rich-text-editor__context-menu-item"
            :aria-checked="isActive(item)"
            @click="apply(item)"
          >
            <i :class="item.icon" class="context__menu-item-icon"></i>
            <span>{{ $t(`richTextEditorBubbleMenu.${item.label}`) }}</span>
          </button>
        </li>
      </ul>
    </div>
  </Context>
</template>

<script>
import Context from '@baserow/modules/core/components/Context'
import { isElement } from '@baserow/modules/core/utils/dom'

const blockItems = [
  { type: 'paragraph', label: 'paragraph', icon: 'baserow-icon-paragraph' },
  {
    type: 'heading',
    level: 1,
    label: 'heading1',
    icon: 'baserow-icon-heading-1',
    preview: 'H1',
  },
  {
    type: 'heading',
    level: 2,
    label: 'heading2',
    icon: 'baserow-icon-heading-2',
    preview: 'H2',
  },
  {
    type: 'heading',
    level: 3,
    label: 'heading3',
    icon: 'baserow-icon-heading-3',
    preview: 'H3',
  },
  {
    type: 'taskList',
    label: 'taskList',
    icon: 'iconoir-task-list',
    preview: '- [ ]',
  },
  { type: 'bulletList', label: 'unorderedList', icon: 'iconoir-list' },
  {
    type: 'orderedList',
    label: 'orderedList',
    icon: 'baserow-icon-ordered-list',
  },
  { type: 'codeBlock', label: 'code', icon: 'iconoir-code' },
]
const markItems = [
  { type: 'bold', label: 'bold', icon: 'iconoir-bold', mark: true },
  { type: 'italic', label: 'italic', icon: 'iconoir-italic', mark: true },
  {
    type: 'underline',
    label: 'underline',
    icon: 'iconoir-underline',
    mark: true,
  },
  {
    type: 'strike',
    label: 'strikethrough',
    icon: 'iconoir-strikethrough',
    mark: true,
  },
]

export default {
  components: { Context },
  props: {
    editor: { type: Object, required: true },
  },
  emits: ['shown', 'hidden'],
  data() {
    return { blockItems, markItems, selection: null }
  },
  computed: {
    hasSelection() {
      return this.selection && this.selection.from !== this.selection.to
    },
  },
  watch: {
    editor() {
      this.$refs.context.hide()
    },
  },
  methods: {
    async show(event) {
      if (!this.editor.isEditable || event.shiftKey) return

      event.preventDefault()
      event.stopPropagation()
      const { from, to, empty } = this.editor.state.selection
      const keyboard =
        event.clientX === 0 && event.clientY === 0 && event.button !== 2
      const origin = keyboard
        ? this.editor.view.coordsAtPos(from)
        : { left: event.clientX, top: event.clientY }
      const clicked = keyboard ? null : this.editor.view.posAtCoords(origin)
      // Keep a selection when right-clicking inside it; otherwise format the
      // clicked line, even when the caret was left in another paragraph.
      if (clicked && (empty || clicked.pos < from || clicked.pos > to)) {
        this.editor.commands.setTextSelection(clicked.pos)
      }
      const selection = this.editor.state.selection
      this.selection = { from: selection.from, to: selection.to }
      this.$emit('shown')
      await this.$refs.context.show(
        { top: origin.top, left: origin.left },
        'bottom',
        'left',
        4,
        0
      )
      const element = this.$refs.context.getTeleportedElement()
      // A tall menu can fit on neither side of the click. Keep all options
      // inside the viewport even in that case.
      const rect = element.getBoundingClientRect()
      element.style.top = `${Math.max(8, Math.min(origin.top + 4, window.innerHeight - rect.height - 8)) + window.scrollY}px`
      element.style.left = `${Math.max(8, Math.min(origin.left, window.innerWidth - rect.width - 8)) + window.scrollX}px`
      element.style.bottom = 'auto'
      element.style.right = 'auto'
      element.querySelector('button')?.focus()
    },
    isActive(item) {
      if (
        item.type === 'paragraph' &&
        ['taskList', 'bulletList', 'orderedList'].some((type) =>
          this.editor.isActive(type)
        )
      ) {
        return false
      }
      return this.editor.isActive(
        item.type,
        item.level ? { level: item.level } : {}
      )
    },
    apply(item) {
      if (!this.selection || !this.editor.isEditable) return
      const chain = this.editor.chain().setTextSelection(this.selection).focus()
      if (item.mark) {
        chain.toggleMark(item.type)
      } else {
        chain.clearNodes()
        switch (item.type) {
          case 'paragraph':
            chain.setParagraph()
            break
          case 'heading':
            chain.setHeading({ level: item.level })
            break
          case 'taskList':
            chain.toggleTaskList()
            break
          case 'bulletList':
            chain.toggleBulletList()
            break
          case 'orderedList':
            chain.toggleOrderedList()
            break
          case 'codeBlock':
            chain.setCodeBlock()
            break
        }
      }
      chain.run()
      this.$refs.context.hide()
    },
    onHidden() {
      this.selection = null
      this.$emit('hidden')
    },
    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Tab') {
        event.preventDefault()
        event.stopPropagation()
        this.$refs.context.hide()
        this.editor.commands.focus()
        return
      }
      const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
      if (!keys.includes(event.key)) return
      event.preventDefault()
      const buttons = Array.from(
        this.$refs.context.getTeleportedElement().querySelectorAll('button')
      )
      const index = buttons.indexOf(document.activeElement)
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? buttons.length - 1
            : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) %
              buttons.length
      buttons[next]?.focus()
    },
    isEventTargetInside(event) {
      const element = this.$refs.context.getTeleportedElement()
      return (
        isElement(element, event.target) ||
        isElement(element, event.relatedTarget)
      )
    },
  },
}
</script>
